import styles from './DeliveryAddressModal.module.scss';

import React, { useCallback } from 'react';
import cx from 'clsx';
import { useSmartNavigate } from 'hooks/useSmartNavigate';
import { Form, FormSubmitHandler } from 'components/Form';
import { ThreePartLayout } from 'components/Layout/ThreePartLayout/ThreePartLayout';
import { ModalKitCloseButton } from 'components/ModalKit/ModalKitCloseButton/ModalKitCloseButton';
import { ModalKitModal } from 'components/ModalKit/ModalKitModal/ModalKitModal';
import { Spinner } from 'components/Spinner';
import { useCloseNavigate } from 'hooks/useCloseNavigate/useCloseNavigate';
import { RouteFC } from 'util/routeTypes';
import { DeliveryAddressModalFooter } from './components/Footer/DeliveryAddressModalFooter';
import { DeliveryAddressModalHeader } from './components/Header/DeliveryAddressModalHeader';
import { DeliveryAddressModalMain } from './components/Main/DeliveryAddressModalMain';
import { DELIVERY_ADDRESS_MODAL } from './DeliveryAddressModal.const';
import { DeliveryAddressFormProvider, useDeliveryAddressForm } from './hooks/useDeliveryAddressForm';
import { useServiceType } from 'hooks/context/serviceType';
import { ServiceType } from 'hooks/context/serviceType/models';

type DeliveryAddressModalLocationStateProps = {
    isFromScheduledOrderModal: boolean;
}

const DeliveryAddressModalInternal: RouteFC = ({ path, location }) => {
    const state = location?.state;
    const { isFromScheduledOrderModal } = state as DeliveryAddressModalLocationStateProps || {};
    const onClose = useCloseNavigate(path);
    const { submit, isPostDeliveriesEstimatePending, isLoadingInitialState } = useDeliveryAddressForm();
    const isLoading = isPostDeliveriesEstimatePending || isLoadingInitialState;
    const navigate = useSmartNavigate();
    const { currentServiceType, chosenServiceType, setChosenServiceType, setRedirectFromAddressModal } = useServiceType();

    const navigateToScheduledOrderModal = useCallback(() => {
        navigate('.edit');
    }, [navigate]);

  const onSubmit: FormSubmitHandler = (e, isValid) => {
    if (isValid) {
      submit(isFromScheduledOrderModal ? navigateToScheduledOrderModal : onClose);
    }
  };

  const handleClose = useCallback(() => {
    if ( chosenServiceType === ServiceType.DELIVERY) {
      setChosenServiceType(currentServiceType);
      setRedirectFromAddressModal(false);
    }
    onClose();
  }, [chosenServiceType, currentServiceType, onClose, setChosenServiceType, setRedirectFromAddressModal]);

  return (
    <ModalKitModal
      desktopHeight={576}
      desktopWidth={900}
      aria={{ labelledby: DELIVERY_ADDRESS_MODAL.title }}
      isOpen
      onClose={handleClose}
      className={cx({ [styles.overflowHidden]: isLoading })}
    >
      <ModalKitCloseButton onClick={handleClose} />
      {isLoading && <Spinner />}
      <Form data-testid={DELIVERY_ADDRESS_MODAL.form} onSubmit={onSubmit} className={styles.layout}>
        <ThreePartLayout className={styles.layout}>
            <ThreePartLayout.Header>
              <DeliveryAddressModalHeader />
            </ThreePartLayout.Header>
            <ThreePartLayout.Main>
              <DeliveryAddressModalMain />
            </ThreePartLayout.Main>
            <ThreePartLayout.Footer>
              <DeliveryAddressModalFooter />
            </ThreePartLayout.Footer>
        </ThreePartLayout>
      </Form>
    </ModalKitModal>
  );
};

export const DeliveryAddressModal: typeof DeliveryAddressModalInternal = (params) => (
  <DeliveryAddressFormProvider>
    <DeliveryAddressModalInternal {...params} />
  </DeliveryAddressFormProvider>
);
